import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, FileText, Clock, CheckCircle2, XCircle, ArrowRight, Building2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/onboarding`;

export default function OnboardingList() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/applications`).then(r => setApps(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (e, appId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this application?")) return;
    try {
      await axios.delete(`${API}/applications/${appId}`);
      setApps(prev => prev.filter(a => a.id !== appId));
    } catch (err) { console.error(err); }
  };

  const statusConfig = {
    draft: { label: "Draft", color: "bg-gray-100 text-gray-600 border-gray-200", icon: FileText },
    submitted: { label: "In Review", color: "bg-blue-100 text-blue-600 border-blue-200", icon: Clock },
    approved: { label: "Approved", color: "bg-green-100 text-green-600 border-green-200", icon: CheckCircle2 },
    declined: { label: "Declined", color: "bg-red-100 text-red-600 border-red-200", icon: XCircle },
  };

  const fmtCurrency = (v) => {
    if (!v) return "—";
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v}`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="onboarding-list-page">
      <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-[#502D3C] tracking-tight" data-testid="page-title">Customer Onboarding</h1>
            <p className="text-[10px] text-[#64748B]">Corporate Card applications and onboarding pipeline</p>
          </div>
          <Button variant="outline" className="text-[#502D3C] border-[#502D3C]/30 hover:bg-[#502D3C]/5 text-sm h-9" onClick={() => navigate("/onboarding/apply")} data-testid="new-application-btn">
            <Plus className="h-4 w-4 mr-1.5" />New Application
          </Button>
        </div>
      </header>

      <main className="p-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6" data-testid="onboarding-stats">
          <Card className="border-gray-100">
            <CardContent className="p-4">
              <p className="text-[10px] font-medium text-[#64748B] uppercase tracking-wider">Total Applications</p>
              <p className="text-2xl font-bold text-[#502D3C] mt-1">{apps.length}</p>
            </CardContent>
          </Card>
          <Card className="border-gray-100">
            <CardContent className="p-4">
              <p className="text-[10px] font-medium text-[#64748B] uppercase tracking-wider">In Review</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{apps.filter(a => a.status === "submitted").length}</p>
            </CardContent>
          </Card>
          <Card className="border-gray-100">
            <CardContent className="p-4">
              <p className="text-[10px] font-medium text-[#64748B] uppercase tracking-wider">Approved</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{apps.filter(a => a.status === "approved").length}</p>
            </CardContent>
          </Card>
          <Card className="border-gray-100">
            <CardContent className="p-4">
              <p className="text-[10px] font-medium text-[#64748B] uppercase tracking-wider">Drafts</p>
              <p className="text-2xl font-bold text-gray-500 mt-1">{apps.filter(a => a.status === "draft").length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Applications Table */}
        {apps.length === 0 ? (
          <Card className="border-dashed border-2 border-gray-200" data-testid="empty-state">
            <CardContent className="py-16 text-center">
              <Building2 className="h-12 w-12 text-[#502D3C]/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#502D3C] mb-1">No applications yet</h3>
              <p className="text-sm text-[#64748B] mb-4">Start a new Corporate Card application to begin the onboarding process.</p>
              <Button variant="outline" className="text-[#502D3C] border-[#502D3C]/30 hover:bg-[#502D3C]/5" onClick={() => navigate("/onboarding/apply")} data-testid="empty-start-btn">
                <Plus className="h-4 w-4 mr-1.5" />Start New Application
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="applications-table">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold text-[#502D3C]">Applications</CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-2">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    {["Company", "EIN", "Source", "Status", "Decision", "Credit Limit", "Updated", ""].map(h => (
                      <th key={h} className="text-left px-5 py-2 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {apps.map(app => {
                    const sc = statusConfig[app.status] || statusConfig.draft;
                    const Icon = sc.icon;
                    return (
                      <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(app.status === "draft" ? `/onboarding/apply?resume=${app.id}` : `/onboarding/status/${app.id}`)} data-testid={`app-row-${app.id}`}>
                        <td className="px-5 py-3 text-sm font-medium text-[#502D3C]">{app.company_name || "—"}</td>
                        <td className="px-5 py-3 text-xs text-[#64748B] font-mono">{app.ein || "—"}</td>
                        <td className="px-5 py-3">{app.is_hris ? <Badge className="bg-green-50 text-green-600 border-green-200 text-[9px] h-5">HRIS</Badge> : <Badge variant="outline" className="text-[9px] h-5">New</Badge>}</td>
                        <td className="px-5 py-3"><Badge variant="outline" className={cn("text-[9px] h-5", sc.color)}><Icon className="h-3 w-3 mr-1" />{sc.label}</Badge></td>
                        <td className="px-5 py-3 text-xs text-[#502D3C]">{app.decision?.risk_class || "—"}</td>
                        <td className="px-5 py-3 text-xs font-medium text-[#502D3C]">{app.decision ? fmtCurrency(app.decision.credit_limit) : "—"}</td>
                        <td className="px-5 py-3 text-[10px] text-[#94A3B8]">{app.updated_at ? new Date(app.updated_at).toLocaleDateString() : "—"}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1.5">
                            <button onClick={(e) => handleDelete(e, app.id)} className="p-1 rounded hover:bg-red-50 text-[#94A3B8] hover:text-red-500 transition-colors" data-testid={`delete-btn-${app.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            <ArrowRight className="h-3.5 w-3.5 text-[#94A3B8]" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
